import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, Download, Mail, Printer, Eye, RefreshCw,
  FileCheck, Truck, Receipt, Search, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import SendByEmailDialog from '../../components/SendByEmailDialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const DocumentsManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [hideTests, setHideTests] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailFiles, setEmailFiles] = useState([]);
  const [emailDefaults, setEmailDefaults] = useState({ subject: '', message: '', recipient: '', vars: {} });

  // Mehrfachauswahl
  const [selected, setSelected] = useState({}); // { "type:id": true }
  // Archiv- & Zeitraum-Filter
  const [archiveMode, setArchiveMode] = useState('active'); // 'active', 'archived', 'all'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Bulk-Email dialog
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkEmailForm, setBulkEmailForm] = useState({ recipient_email: '', subject: '', message: '' });
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [archiveMode, fromDate, toDate]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ archive_mode: archiveMode });
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const response = await axiosInstance.get(`/documents/admin/all?${params.toString()}`);
      setDocuments(response.data.documents || []);
      // Wipe selections that no longer match the current view
      setSelected({});
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const docKey = (d) => `${d.type}:${d.id}`;
  const selectedDocs = () => documents.filter((d) => selected[docKey(d)]);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleSelect = (d) => {
    setSelected((prev) => ({ ...prev, [docKey(d)]: !prev[docKey(d)] }));
  };

  const toggleAllVisible = (visible) => {
    if (selectedCount === visible.length) {
      setSelected({});
    } else {
      const next = {};
      visible.forEach((d) => { next[docKey(d)] = true; });
      setSelected(next);
    }
  };

  const buildPayload = () => ({
    documents: selectedDocs().map((d) => ({ type: d.type, id: d.id })),
  });

  const bulkArchive = async () => {
    if (!selectedCount) return toast.error('Bitte zuerst Dokumente auswählen');
    if (!window.confirm(`${selectedCount} Dokument(e) ins Archiv verschieben?\n\nSie bleiben erhalten und können jederzeit über den Filter „Nur Archiv" zurückgeholt werden.`)) return;
    try {
      setBulkBusy(true);
      const r = await axiosInstance.post('/documents/admin/bulk-archive', buildPayload());
      toast.success(r.data.message);
      loadDocuments();
    } catch (e) {
      toast.error('Archivierung fehlgeschlagen');
    } finally { setBulkBusy(false); }
  };

  const bulkRestore = async () => {
    if (!selectedCount) return toast.error('Bitte zuerst Dokumente auswählen');
    try {
      setBulkBusy(true);
      const r = await axiosInstance.post('/documents/admin/bulk-archive', { ...buildPayload(), restore: true });
      toast.success(r.data.message);
      loadDocuments();
    } catch (e) {
      toast.error('Wiederherstellung fehlgeschlagen');
    } finally { setBulkBusy(false); }
  };

  const bulkDownloadZip = async () => {
    if (!selectedCount) return toast.error('Bitte zuerst Dokumente auswählen');
    try {
      setBulkBusy(true);
      const r = await axiosInstance.post('/documents/admin/bulk-zip', buildPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dokumente_${today}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedCount} Dokument(e) als ZIP heruntergeladen`);
    } catch (e) {
      toast.error('ZIP-Download fehlgeschlagen');
    } finally { setBulkBusy(false); }
  };

  const openBulkEmail = () => {
    if (!selectedCount) return toast.error('Bitte zuerst Dokumente auswählen');
    const today = new Date().toLocaleDateString('de-DE');
    setBulkEmailForm({
      recipient_email: '',
      subject: `Dokumenten-Export Homeyoursoul (${today}) – ${selectedCount} Datei(en)`,
      message: `Hallo,\n\nanbei finden Sie ${selectedCount} Dokument(e) aus Homeyoursoul.\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul`,
    });
    setBulkEmailOpen(true);
  };

  const sendBulkEmail = async () => {
    if (!bulkEmailForm.recipient_email.trim()) return toast.error('Empfänger-E-Mail fehlt');
    try {
      setBulkBusy(true);
      const r = await axiosInstance.post('/documents/admin/bulk-email', {
        ...buildPayload(),
        recipient_email: bulkEmailForm.recipient_email.trim(),
        subject: bulkEmailForm.subject,
        message: bulkEmailForm.message,
      });
      toast.success(r.data.message);
      setBulkEmailOpen(false);
    } catch (e) {
      toast.error('E-Mail-Versand fehlgeschlagen: ' + (e?.response?.data?.detail || ''));
    } finally { setBulkBusy(false); }
  };

  // Quick presets for date range
  const setPresetRange = (preset) => {
    const today = new Date();
    let from, to;
    if (preset === 'this_month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = today;
    } else if (preset === 'last_month') {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === 'this_year') {
      from = new Date(today.getFullYear(), 0, 1);
      to = today;
    } else {
      from = null; to = null;
    }
    setFromDate(from ? from.toISOString().slice(0, 10) : '');
    setToDate(to ? to.toISOString().slice(0, 10) : '');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'invoice':
        return <Receipt className="h-5 w-5 text-blue-600" />;
      case 'delivery_note':
        return <Truck className="h-5 w-5 text-green-600" />;
      case 'order_confirmation':
        return <FileCheck className="h-5 w-5 text-purple-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'paid': { label: 'Bezahlt', variant: 'default', className: 'bg-green-100 text-green-800' },
      'pending': { label: 'Ausstehend', variant: 'secondary', className: 'bg-yellow-100 text-yellow-800' },
      'shipped': { label: 'Versendet', variant: 'default', className: 'bg-blue-100 text-blue-800' },
      'delivered': { label: 'Geliefert', variant: 'default', className: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'Storniert', variant: 'destructive', className: 'bg-red-100 text-red-800' },
      'overdue': { label: 'Überfällig', variant: 'destructive', className: 'bg-red-100 text-red-800' }
    };
    const config = statusMap[status] || { label: status, variant: 'secondary', className: '' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const downloadPDF = async (doc) => {
    try {
      let endpoint = '';
      let filename = '';
      
      switch (doc.type) {
        case 'invoice':
          endpoint = `/documents/invoice/${doc.id}/pdf`;
          filename = `Rechnung_${doc.number}.pdf`;
          break;
        case 'delivery_note':
          endpoint = `/documents/order/${doc.id}/delivery-note/pdf`;
          filename = `Lieferschein_${doc.number}.pdf`;
          break;
        case 'order_confirmation':
          endpoint = `/documents/order/${doc.id}/confirmation/pdf`;
          filename = `Bestellbestaetigung_${doc.number}.pdf`;
          break;
        default:
          toast.error('Unbekannter Dokumenttyp');
          return;
      }
      
      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Herunterladen');
    }
  };

  const previewPDF = async (doc) => {
    try {
      let endpoint = '';
      
      switch (doc.type) {
        case 'invoice':
          endpoint = `/documents/invoice/${doc.id}/pdf`;
          break;
        case 'delivery_note':
          endpoint = `/documents/order/${doc.id}/delivery-note/pdf`;
          break;
        case 'order_confirmation':
          endpoint = `/documents/order/${doc.id}/confirmation/pdf`;
          break;
        default:
          return;
      }
      
      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewTitle(`${doc.type_label}: ${doc.number}`);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const printPDF = async (doc) => {
    try {
      let endpoint = '';
      
      switch (doc.type) {
        case 'invoice':
          endpoint = `/documents/invoice/${doc.id}/pdf`;
          break;
        case 'delivery_note':
          endpoint = `/documents/order/${doc.id}/delivery-note/pdf`;
          break;
        case 'order_confirmation':
          endpoint = `/documents/order/${doc.id}/confirmation/pdf`;
          break;
        default:
          return;
      }
      
      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Fehler beim Drucken');
    }
  };

  const sendEmail = (doc) => {
    // Open the global SendByEmailDialog with the PDF as attachment
    let endpoint = '';
    switch (doc.type) {
      case 'invoice':
        endpoint = `/documents/invoice/${doc.id}/pdf`;
        break;
      case 'delivery_note':
        endpoint = `/documents/order/${doc.id}/delivery-note/pdf`;
        break;
      case 'order_confirmation':
        endpoint = `/documents/order/${doc.id}/confirmation/pdf`;
        break;
      default:
        toast.error('Unbekannter Dokumenttyp');
        return;
    }
    setEmailFiles([{
      filename: `${doc.number || doc.id}.pdf`,
      getBlob: async () => {
        const res = await axiosInstance.get(endpoint, { responseType: 'blob' });
        return new Blob([res.data], { type: 'application/pdf' });
      },
    }]);
    setEmailDefaults({
      recipient: doc.customer_email || '',
      subject: `Ihr ${doc.type_label} ${doc.number || ''} von Homeyoursoul`.trim(),
      message: `Sehr geehrte/r ${doc.customer_name || 'Kundin / Kunde'},\n\nanbei senden wir Ihnen Ihr Dokument ${doc.number || ''}.\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul`,
      vars: {
        Kundenname: doc.customer_name || '',
        Rechnungsnummer: doc.number || '',
      },
    });
    setEmailOpen(true);
  };

  // Filter documents
  const filteredDocuments = useMemo(() => {
    const term = searchQuery.toLowerCase();
    return documents.filter((doc) => {
      if (filterType !== 'all' && doc.type !== filterType) return false;
      if (hideTests && doc.is_test) return false;
      if (!term) return true;
      return (
        (doc.number || '').toLowerCase().includes(term) ||
        (doc.customer_name || '').toLowerCase().includes(term) ||
        (doc.customer_email || '').toLowerCase().includes(term)
      );
    });
  }, [documents, filterType, searchQuery, hideTests]);

  // Stats
  const stats = {
    total: documents.length,
    invoices: documents.filter(d => d.type === 'invoice').length,
    deliveryNotes: documents.filter(d => d.type === 'delivery_note').length,
    confirmations: documents.filter(d => d.type === 'order_confirmation').length,
    tests: documents.filter(d => d.is_test).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Dokumente</h1>
              <p className="text-sm text-gray-600">Rechnungen, Lieferscheine & Bestellbestätigungen</p>
            </div>
          </div>
          <Button onClick={loadDocuments} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card data-testid="docs-stat-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FileText className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-gray-500">Gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="docs-stat-invoices">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.invoices}</p>
                  <p className="text-xs text-gray-500">Rechnungen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="docs-stat-delivery">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Truck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.deliveryNotes}</p>
                  <p className="text-xs text-gray-500">Lieferscheine</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="docs-stat-confirmations">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileCheck className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.confirmations}</p>
                  <p className="text-xs text-gray-500">Bestätigungen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="docs-stat-tests" className={stats.tests > 0 ? 'border-yellow-400 border-2' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-800">{stats.tests}</p>
                  <p className="text-xs text-gray-500">davon Tests</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Box about Email */}
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-amber-900">
              <strong>E-Mail-Versand:</strong> Über das Briefumschlag-Symbol kannst du jedes Dokument einzeln per E-Mail an den Kunden senden — mit Vorlagen, CC/BCC und individueller Nachricht.
            </p>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4 space-y-3">
            {/* Suche + Typ + Tests */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Suchen nach Nummer, Kunde oder E-Mail..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="docs-search"
                  />
                </div>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  data-testid="docs-filter-type"
                >
                  <option value="all">Alle Typen</option>
                  <option value="invoice">Rechnungen</option>
                  <option value="delivery_note">Lieferscheine</option>
                  <option value="order_confirmation">Bestätigungen</option>
                </select>
                <select
                  value={archiveMode}
                  onChange={(e) => setArchiveMode(e.target.value)}
                  className="h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  data-testid="docs-archive-mode"
                  title="Welche Dokumente anzeigen"
                >
                  <option value="active">Aktiv</option>
                  <option value="archived">Nur Archiv</option>
                  <option value="all">Alle (inkl. Archiv)</option>
                </select>
                <label className="flex items-center gap-2 text-xs text-gray-700 whitespace-nowrap cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hideTests}
                    onChange={(e) => setHideTests(e.target.checked)}
                    className="accent-[#C4A084]"
                    data-testid="docs-hide-tests"
                  />
                  Tests ausblenden
                </label>
              </div>
            </div>
            {/* Zeitraum-Filter */}
            <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-[#E8DCC8]">
              <span className="text-xs font-medium text-[#5D4E3C]">Zeitraum:</span>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-9 w-auto text-sm"
                data-testid="docs-from-date"
                aria-label="Von Datum"
              />
              <span className="text-xs text-[#8B7355]">bis</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-9 w-auto text-sm"
                data-testid="docs-to-date"
                aria-label="Bis Datum"
              />
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => setPresetRange('this_month')} className="text-xs px-2 py-1 bg-[#FAF5EE] hover:bg-[#F5EDE4] border border-[#D4C4B0] rounded" data-testid="docs-preset-this-month">Aktueller Monat</button>
                <button onClick={() => setPresetRange('last_month')} className="text-xs px-2 py-1 bg-[#FAF5EE] hover:bg-[#F5EDE4] border border-[#D4C4B0] rounded" data-testid="docs-preset-last-month">Letzter Monat</button>
                <button onClick={() => setPresetRange('this_year')} className="text-xs px-2 py-1 bg-[#FAF5EE] hover:bg-[#F5EDE4] border border-[#D4C4B0] rounded" data-testid="docs-preset-this-year">Dieses Jahr</button>
                {(fromDate || toDate) && (
                  <button onClick={() => { setFromDate(''); setToDate(''); }} className="text-xs px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded" data-testid="docs-preset-clear">Zurücksetzen</button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk-Aktionen Toolbar */}
        {selectedCount > 0 && (
          <Card className="mb-6 border-[#C4A084] border-2" data-testid="docs-bulk-toolbar">
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-[#5D4E3C]">
                <strong>{selectedCount}</strong> Dokument(e) ausgewählt
              </span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => setSelected({})} className="rounded-none border-[#D4C4B0]" data-testid="docs-bulk-clear">
                Auswahl löschen
              </Button>
              <Button size="sm" onClick={bulkDownloadZip} disabled={bulkBusy} className="rounded-none bg-[#C4A084] hover:bg-[#B89968] text-white" data-testid="docs-bulk-zip">
                <Download className="h-3.5 w-3.5 mr-1" /> Als ZIP downloaden
              </Button>
              <Button size="sm" onClick={openBulkEmail} disabled={bulkBusy} className="rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white" data-testid="docs-bulk-email">
                <Mail className="h-3.5 w-3.5 mr-1" /> Per E-Mail senden
              </Button>
              {archiveMode === 'archived' ? (
                <Button size="sm" variant="outline" onClick={bulkRestore} disabled={bulkBusy} className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="docs-bulk-restore">
                  ↩ Wiederherstellen
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={bulkArchive} disabled={bulkBusy} className="rounded-none border-[#8B6F4D] text-[#5D4E3C] hover:bg-[#F5EDE4]" data-testid="docs-bulk-archive">
                  📦 Archivieren
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-2 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={filteredDocuments.length > 0 && filteredDocuments.every((d) => selected[docKey(d)])}
                        onChange={() => toggleAllVisible(filteredDocuments)}
                        className="accent-[#C4A084] cursor-pointer"
                        title="Alle sichtbaren auswählen / abwählen"
                        aria-label="Alle auswählen"
                        data-testid="docs-select-all"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Typ</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Nummer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Kunde</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Betrag</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredDocuments.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>Keine Dokumente gefunden</p>
                      </td>
                    </tr>
                  ) : (
                    filteredDocuments.map((doc) => (
                      <tr key={`${doc.type}-${doc.id}`} className={`hover:bg-gray-50 ${doc.is_test ? 'bg-yellow-50/60 border-l-4 border-l-yellow-400' : ''} ${doc.archived_at ? 'opacity-70' : ''}`} data-testid={`docs-row-${doc.type}-${doc.id}`}>
                        <td className="px-2 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={!!selected[docKey(doc)]}
                            onChange={() => toggleSelect(doc)}
                            className="accent-[#C4A084] cursor-pointer"
                            aria-label={`„${doc.number}" auswählen`}
                            data-testid={`docs-select-${doc.type}-${doc.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getTypeIcon(doc.type)}
                            <span className="text-sm font-medium">{doc.type_label}</span>
                            {doc.is_test && (
                              <Badge className="bg-yellow-300 text-yellow-900 border border-yellow-500 text-[10px] font-bold uppercase tracking-wide flex items-center gap-1" data-testid={`docs-test-badge-${doc.id}`}>
                                <AlertTriangle className="h-3 w-3" /> TEST
                              </Badge>
                            )}
                            {doc.archived_at && (
                              <Badge className="bg-gray-200 text-gray-700 border border-gray-300 text-[10px] font-medium" data-testid={`docs-archive-badge-${doc.id}`} title={`Archiviert am ${doc.archived_at.slice(0, 10)}`}>
                                📦 Archiv
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-mono">{doc.number}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{doc.date}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-[200px]">
                            {doc.customer_name && (
                              <p className="text-sm font-medium text-gray-800 truncate">{doc.customer_name}</p>
                            )}
                            <p className="text-xs text-gray-500 truncate">{doc.customer_email || '—'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold">{formatEUR(doc.total)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(doc.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => previewPDF(doc)}
                              title="Vorschau"
                              data-testid={`docs-preview-${doc.type}-${doc.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => downloadPDF(doc)}
                              title="Herunterladen"
                              data-testid={`docs-download-${doc.type}-${doc.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => printPDF(doc)}
                              title="Drucken"
                              data-testid={`docs-print-${doc.type}-${doc.id}`}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => sendEmail(doc)}
                              title="Per E-Mail senden"
                              data-testid={`docs-email-${doc.type}-${doc.id}`}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>{previewTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-[calc(90vh-80px)]"
                title="PDF Vorschau"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SendByEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        files={emailFiles}
        defaultSubject={emailDefaults.subject}
        defaultMessage={emailDefaults.message}
        defaultRecipient={emailDefaults.recipient}
        templateVars={emailDefaults.vars || {}}
      />

      {/* Bulk Email Dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
        <DialogContent className="max-w-lg" data-testid="docs-bulk-email-dialog">
          <DialogHeader>
            <DialogTitle>{selectedCount} Dokument(e) per E-Mail senden</DialogTitle>
            <DialogDescription>
              Die ausgewählten PDFs werden als Anhang verschickt. Tipp: Schick sie an deine eigene
              E-Mail-Adresse oder direkt an deine Steuerberatung.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-[#5D4E3C]">Empfänger E-Mail *</label>
              <Input
                type="email"
                value={bulkEmailForm.recipient_email}
                onChange={(e) => setBulkEmailForm({ ...bulkEmailForm, recipient_email: e.target.value })}
                placeholder="z.B. yasemink1213@gmail.com"
                data-testid="docs-bulk-email-recipient"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5D4E3C]">Betreff</label>
              <Input
                value={bulkEmailForm.subject}
                onChange={(e) => setBulkEmailForm({ ...bulkEmailForm, subject: e.target.value })}
                data-testid="docs-bulk-email-subject"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5D4E3C]">Nachricht</label>
              <textarea
                rows={5}
                value={bulkEmailForm.message}
                onChange={(e) => setBulkEmailForm({ ...bulkEmailForm, message: e.target.value })}
                className="w-full px-3 py-2 border border-[#D4C4B0] rounded text-sm"
                data-testid="docs-bulk-email-message"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkEmailOpen(false)} className="rounded-none border-[#D4C4B0]">Abbrechen</Button>
            <Button onClick={sendBulkEmail} disabled={bulkBusy || !bulkEmailForm.recipient_email} className="rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white" data-testid="docs-bulk-email-send">
              <Mail className="h-4 w-4 mr-2" /> {bulkBusy ? 'Sende…' : 'Senden'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsManager;
